import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { opsService, type ContainerMetrics } from '../../services/opsService';

export function InfrastructureServicesPage() {
  const [rows, setRows] = useState<ContainerMetrics[]>([]);
  const [monitored, setMonitored] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await opsService.containers();
      setRows(res.containers ?? []);
      setMonitored(res.monitored ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">سرویس‌ها</h1>
          <p className="mt-1 text-sm text-slate-500">
            فقط کانتینرهای allowlist مانیتور می‌شوند. عامل هیچ ری‌استارتی انجام نمی‌دهد.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      {monitored.length > 0 ? (
        <p className="text-xs text-slate-500">مانیتور: {monitored.join(' · ')}</p>
      ) : null}

      <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-right font-semibold">نام</th>
              <th className="px-4 py-3 text-right font-semibold">وضعیت</th>
              <th className="px-4 py-3 text-right font-semibold">سلامت</th>
              <th className="px-4 py-3 text-right font-semibold">CPU</th>
              <th className="px-4 py-3 text-right font-semibold">حافظه</th>
              <th className="px-4 py-3 text-right font-semibold">ری‌استارت</th>
              <th className="px-4 py-3 text-right font-semibold">ایمیج</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  در حال بارگذاری…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  داده‌ای نیست
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <tr key={c.name} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-3">
                    {!c.available ? (
                      <span className="text-rose-600">{c.error || 'unavailable'}</span>
                    ) : (
                      <span className={c.running ? 'text-emerald-700' : 'text-rose-700'}>{c.status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{c.health || '—'}</td>
                  <td className="px-4 py-3">{c.cpuPercent?.toFixed(1) ?? '—'}%</td>
                  <td className="px-4 py-3">
                    {c.memUsedMb?.toFixed(0) ?? '—'} / {c.memLimitMb?.toFixed(0) ?? '—'} MB
                  </td>
                  <td className="px-4 py-3">{c.restartCount}</td>
                  <td className="max-w-[220px] truncate px-4 py-3 text-xs text-slate-500" title={c.image}>
                    {c.image || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
